import PubSub from "pubsub-js";
import { type FC, useMemo, useState } from "react";

import { TabDialog, TabItem, TriggerButton, formatDateTimeFromISO } from "widgets";
import { type ControlledDialog, useControlledDialog } from "widgets/hooks";

import { PubSubActions } from "@/global/pubsub-actions.ts";
import { OpenInNewTabButton } from "@/modules/core/components/OpenInNewTabButton";
import { FileCard } from "@/modules/files/components/FileCard";
import { FileDownloadButton } from "@/modules/files/components/FileDownloadButton";
import { FilePreviewControlledDialog } from "@/modules/files/components/FilePreviewControlledDialog";
import { HeaderIcon } from "@/modules/files/components/HeaderIcon";
import { useGetFileInfoCardQuery } from "@/modules/files/graphql/files-queries.gql";
import { FILE_BROWSE_TYPES } from "@/settings/settings.ts";
import { FileEditDialogForm } from "@/ui-modules/files/FileEditDialog.tsx";
import { FileEventsCard } from "@/ui-modules/files/FileEventsCard.tsx";
import { FileResourcesCard } from "@/ui-modules/files/FileResourcesCard.tsx";

interface FileDetailsDialogProps extends ControlledDialog {
  fileId: number;
  /** Callback passed to the Edit Dialog */
  onSaved?: () => void;
}

export const FileDetailsDialog: FC<FileDetailsDialogProps> = (props) => {
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState<boolean>(false);
  const { dialogProps, underlayProps, dialogRef } = useControlledDialog(props.onCancel);
  // Loaded within <FileCard>
  const { data, refetch } = useGetFileInfoCardQuery({
    variables: { fileId: props.fileId },
  });

  const [file, isRegularFile, isFolder, isHyperlink] = useMemo(() => {
    const file = data?.projectFileById;

    return [
      file,
      file?.fileBrowseType === FILE_BROWSE_TYPES.REGULAR_FILE,
      file?.fileBrowseType === FILE_BROWSE_TYPES.DIRECTORY,
      file?.fileBrowseType === FILE_BROWSE_TYPES.HYPERLINK,
    ];
  }, [data]);

  const actionWidgets = [];

  if ((isRegularFile || isFolder || isHyperlink) && file?.isEditable && props.onSaved) {
    actionWidgets.push(
      <TriggerButton
        icon={"edit"}
        onPress={() => setShowEditDialog(true)}
        tooltip={"Edit some settings of this file"}
        key={"files-page-action-edit"}
        label={"Edit"}
        showLabel
      />,
    );
  }
  if (isHyperlink) {
    actionWidgets.push(<OpenInNewTabButton url={file?.url} key={"files-page-action-open"} />);
  }
  if (file?.previewable) {
    actionWidgets.push(
      <TriggerButton
        onPress={() => {
          setShowPreviewDialog(true);
        }}
        icon={"zoom_in"}
        key={"file-details-dialog-preview-open"}
        label={"Preview"}
        showLabel
      />,
    );
  }

  return (
    <>
      <TabDialog
        ref={dialogRef}
        title={file?.fileName ?? ""}
        subtitle={
          file?.lastModified
            ? `Modified on ${formatDateTimeFromISO(file?.lastModified, { hideTime: true })}`
            : undefined
        }
        icon={<HeaderIcon file={file} />}
        closeFn={() => props.onCancel?.()}
        domProps={dialogProps}
        underlayProps={underlayProps}
        width={580}
        actions={[...actionWidgets, <FileDownloadButton file={file} key={`file-details-dialog-download-button`} />]}
        disabledKeys={(isFolder || isHyperlink) && [`file-info-dialog-resources-tab`, `file-info-dialog-events-tab`]}
      >
        <TabItem key={`file-info-dialog-file-tab`} title={"Info"}>
          <FileCard fileId={props.fileId} scrollable noBorder contentStyle={{ paddingTop: 20 }} />
        </TabItem>
        <TabItem key={`file-info-dialog-resources-tab`} title={"Associated Resources"}>
          <FileResourcesCard fileId={props.fileId} scrollable noBorder contentStyle={{ paddingTop: 20 }} />
        </TabItem>
        <TabItem key={`file-info-dialog-events-tab`} title={"Related Events"}>
          <FileEventsCard fileId={props.fileId} scrollable noBorder contentStyle={{ paddingTop: 20 }} />
        </TabItem>
      </TabDialog>
      {showEditDialog && file && props.onSaved && (
        <FileEditDialogForm
          fileId={props.fileId}
          onSaved={() => {
            void refetch();
            PubSub.publish(PubSubActions.FILE_EDIT);
            setShowEditDialog(false);
            props.onSaved();
          }}
          onCancel={() => setShowEditDialog(false)}
        />
      )}

      {showPreviewDialog && (
        <FilePreviewControlledDialog
          fileId={props.fileId}
          key={`file-details-dialog-preview-button`}
          onCancel={() => setShowPreviewDialog(false)}
        />
      )}
    </>
  );
};
